<template>
  <div v-if="visibleWebAds.length" class="wc-web-ads-container px-0">
    <WCWebAdModern v-if="visibleWebAds.length == 1" :web-ad="visibleWebAds[0]" />
    <WCAutoplayCarouselModern v-else class="px-0 position-relative">
      <SplideSlide v-for="webAd in visibleWebAds" :key="webAd.id">
        <WCWebAdModern :web-ad="webAd" />
      </SplideSlide>
    </WCAutoplayCarouselModern>
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults } from 'vue';
import { SplideSlide } from '@splidejs/vue-splide';
import WebAd from '@/models/entities/web-ad';
import usePlatform from '@/modules/platform/composables/usePlatform';
import WCWebAdModern from '../WCWebAdModern/WCWebAdModern.vue';
import WCAutoplayCarouselModern from '../WCAutoplayCarouselModern/WCAutoplayCarouselModern.vue';

const props = withDefaults(
  defineProps<{
    webAds?: WebAd[];
  }>(),
  {
    webAds: () => [],
  },
);

const { platform } = usePlatform();

const mobileWebAds = computed(() => props.webAds.filter(webAd => webAd.type === 2));
const desktopWebAds = computed(() => props.webAds.filter(webAd => webAd.type === 1));

const isDesktopOrTablet = computed(() => !platform.value.isMobile || platform.value.isMediumDevice);

const visibleWebAds = computed(() => {
  if (isDesktopOrTablet.value && desktopWebAds.value.length > 0) {
    return desktopWebAds.value.slice(0, 6);
  }
  if (platform.value.isMobile && mobileWebAds.value.length > 0) {
    return mobileWebAds.value.slice(0, 6);
  }

  return props.webAds.slice(0, 6);
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

$web-ad-dots-row-height: 1.625rem;

.wc-web-ads-container {
  padding-bottom: $web-ad-dots-row-height;
  padding-left: 2.5rem !important;
  padding-right: 2.5rem !important;
}

@include media-breakpoint-up(lg) {
  .wc-web-ads-container {
    width: 100%;
  }
}

:deep(.splide__pagination) {
  position: absolute !important;
  top: 100% !important;
  bottom: auto !important;
  margin-top: 0.5rem;
  padding-bottom: 0.625rem;
  gap: 0.375rem;
  background: var(--white, $white);
}

@include media-breakpoint-up(lg) {
  :deep(.splide__pagination) {
    background: transparent;
  }
}

:deep(.splide__pagination__page) {
  width: 0.5rem !important;
  height: 0.5rem !important;
  margin: 0 !important;
  border: none;
  border-radius: 50%;
  background: var(--dark-lighter-8);
  opacity: 1;
}

:deep(.splide__pagination__page.is-active) {
  background: var(--primary, $primary);
  transform: scale(1);
}
</style>
